// src/lib/api.js
import { SupabaseClient } from '../supabaseClient'

/**
 * Pull all weight entries for a profile from a given cutoff date onward,
 * ordered oldest→newest.
 */
export async function fetchWeightEntries(profileId, isoCutoffDate = null) {
    let query = SupabaseClient
        .from('weight_entries')
        .select('weight_kg, date')
        .eq('profile_id', profileId)

    if (isoCutoffDate) {
        query = query.gte('date', isoCutoffDate)
    }

    const { data, error } = await query.order('date', { ascending: true })

    if (error) throw error
    return data  // [{ weight_kg: 68.2, date: "2025-04-23" }, …]
}


/**
 * Fetches the total calories consumed each day over the past `days` calendar days.
 */
export async function fetchDailyCalories(profileId, days = 90) {
    // 1) calculate cutoff date (including today in the count)
    const today  = new Date()
    const cutoff = new Date(today)
    cutoff.setDate(today.getDate() - (days - 1))
    const isoCutoff = cutoff.toISOString().split('T')[0]

    // 2) load meal_logs + their meal_nutrients for the window
    const { data: logs, error } = await SupabaseClient
        .from('meal_logs')
        .select('meal_date, meal_nutrients(calories)')
        .eq('profile_id', profileId)
        .gte('meal_date', isoCutoff)
        .order('meal_date', { ascending: true })

    if (error) throw error

    // 3) sum calories by date
    const dailyTotals = logs.reduce((acc, { meal_date, meal_nutrients }) => {
        const dayCalories = Array.isArray(meal_nutrients)
            ? meal_nutrients.reduce((sum, n) => sum + (n.calories || 0), 0)
            : 0

        acc[meal_date] = (acc[meal_date] || 0) + dayCalories
        return acc
    }, /** @type Record<string, number> */({}))

    // 4) build a days-long array, filling zeros where needed
    const result = []
    for (let i = 0; i < days; i++) {
        const dt = new Date(cutoff)
        dt.setDate(cutoff.getDate() + i)
        const key = dt.toISOString().split('T')[0]
        result.push({
            date: key,
            calories: dailyTotals[key] || 0
        })
    }

    return result  // [{ date: "2025-04-17", calories: 0 }, …]
}


/**
 * Estimate your TDEE over the past N days by combining:
 *  • avg intake on days with both a calorie log & weight log
 *  • observed weight change (Δkg → Δcal)  only if ≥2 matching days
 *  • profile BMR via Mifflin–St Jeor       only if ≥1 weight log
 */
export async function estimateTDEE(profileId, days = 7) {
    // 1) load profile
    const { data: profile, error: pErr } = await SupabaseClient
        .from('profiles')
        .select('height, gender, date_of_birth')
        .eq('id', profileId)
        .single()
    if (pErr) throw pErr

    // 2) calculate cutoff
    const today  = new Date()
    const cutoff = new Date(today)
    cutoff.setDate(today.getDate() - (days - 1))
    const isoCutoff = cutoff.toISOString().split('T')[0]

    // 3) fetch data
    const daily      = await fetchDailyCalories(profileId, days)
    const weights    = await fetchWeightEntries(profileId, isoCutoff)

    // 4) build maps
    const intakeMap = Object.fromEntries(daily.map(d => [d.date, d.calories]))
    const weightMap = Object.fromEntries(weights.map(w => [w.date, w.weight_kg]))

    // 5) find dates with both intake & weight
    const dates = daily
        .map(d => d.date)
        .filter(date => intakeMap[date] > 0 && weightMap[date] != null)

    const count = dates.length
    const totalIntake = dates.reduce((sum, date) => sum + intakeMap[date], 0)
    const avgIntake   = count > 0 ? totalIntake / count : 0

    // baseline
    let maintenance    = avgIntake
    let bmr            = null
    let activityFactor = null

    if (count >= 1) {
        // sort to get first/last
        const sorted = [...dates].sort()
        const firstW = weightMap[sorted[0]]
        const lastW  = weightMap[sorted[sorted.length - 1]]

        // adjust for weight change only if ≥2 days
        if (count >= 2) {
            const deltaKg   = lastW - firstW
            const avgCalAdj = (deltaKg * 7700) / (count - 1)
            maintenance     = avgIntake - avgCalAdj
        }

        // Mifflin–St Jeor BMR
        const age = (() => {
            const dob  = new Date(profile.date_of_birth)
            const diff = Date.now() - dob.getTime()
            return new Date(diff).getUTCFullYear() - 1970
        })()
        const s   = profile.gender === 'male' ?  5 : -161
        bmr       = 10 * lastW + 6.25 * profile.height - 5 * age + s

        activityFactor = bmr > 0 ? maintenance / bmr : 0
    }

    return { maintenance, bmr, activityFactor }
}